import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { media } from '../../utils/media-queries';
import RichText from '../atoms/richtext';
import Accordion from '../molecules/accordion';
import { LayoutContext } from './layoutProvider';

const Team = ({
  members,
  style,
  className,
  onClick,
  headline,
  dark,
  zIndex = 1,
  inactive,
}) => {
  const ref = useRef();
  const [height, setHeight] = useState();
  const [clickedAcc, setClickedAcc] = useState(null);

  const { setTeamHeight } = useContext(LayoutContext);

  useEffect(() => {

    setHeight(ref.current.getBoundingClientRect().height - 200);

    if (ref.current.className.includes('team-1')) setTeamHeight(height + 500);
    if (ref.current.className.includes('team-2')) setTeamHeight(height + 500);
    if (ref.current.className.includes('team-3')) setTeamHeight(height + 700);

    if (zIndex === 1) {
      setClickedAcc(null);
    }
  }, [ref, height, setTeamHeight, zIndex]);

  const handleClick = (i) => {
    if (clickedAcc === i) {
      setClickedAcc(null);
      return;
    }
    setClickedAcc(i);
  };

  return (
    <StyledWrapper style={{ zIndex }} className={className} onClick={onClick}>
      <StyledTeam
        className={className}
        ref={ref}
        onClick={() =>
          setTimeout(
            () => setHeight(ref.current.getBoundingClientRect().height - 200),
            300
          )
        }
      >
        <h3>{headline}</h3>
        {members.map((member, i) => (
          <Accordion
            dark={dark}
            key={i}
            light={false}
            headline={member.name}
            subheadline={member.position}
            className="team"
            style={style}
            last={members.length === i + 1}
            i={i}
            onClick={() => handleClick(i)}
            active={clickedAcc === i}
            inactive={inactive}
          >
            <div className="team-content" style={{ justifyContent: 'start' }}>
              <div className="text">
                <RichText blocks={member._rawDescription} />
                {member.linkedin && (
                  <p className="linkedin">
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      LinkedIn
                    </a>
                  </p>
                )}
              </div>
              <figure style={{ aspectRatio: '1/1', overflow: 'hidden' }}>
                <GatsbyImage
                  image={member.portrait?.asset.gatsbyImageData}
                  alt={member.name}
                />
              </figure>
            </div>
          </Accordion>
        ))}
      </StyledTeam>
    </StyledWrapper>
  );
};
const StyledWrapper = styled.div`
  width: calc(100vw - var(--sp-1-8));
  margin-bottom: var(--sp-3-5);
  transition: height 0.3s;
  position: absolute;
  pointer-events:none;

  &.inactive {
    cursor:pointer;
  }

  &.team-1 {
    z-index: 3;
  }

  &.team-2 {
    z-index: 2;
    transform: translateY(10rem);

    @media ${media.M} {
      transform: translateY(12.5rem);
    }

    @media ${media.XL} {
      transform: translateY(15rem);
    }
  }

  &.team-3 {
    z-index: 1;
    transform: translateY(20rem);

    @media ${media.M} {
      transform: translateY(25rem);
    }

    @media ${media.XL} {
      transform: translateY(30rem);
    }
  }


  @media ${media.M} {
    margin-bottom: var(--sp-8);
  }
`;

const StyledTeam = styled(motion.div)`
  padding-bottom: var(--sp-3-5);
  width: 100%;
  pointer-events:auto;

  h3 {
    padding-left: var(--sp-1-3);
    padding-top: var(--sp-3-5);
    padding-bottom: var(--sp-3-5);
    font-size: 2.4rem;
    color: var(--red);
    font-weight: 300;
  }

  .collapse {
   
    .text {
      padding-bottom: var(--sp-8);
    }
  }

  .team-content {
    padding-left: var(--sp-1-3);
    display: flex;
    padding-right: var(--sp-1-3);
    flex-direction: column;
    font-size:1.4rem;

    .text {
      order: 2;
      padding-right: var(--sp-1-3);
      width: 100%;
      overflow: scroll;
      max-height: 45rem;
      mask-image:linear-gradient(
        to bottom,
        black 70%,
        transparent 100%
      );

      scrollbar-width: none;

      p {
        margin:2rem 0;

        &:first-child {
          margin-top:0;
        }

        &:last-child {
          margin-bottom:0;
        }
      }

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .linkedin {
      margin-top: var(--sp-3-5);
    }

    figure {
      order: 1;
      margin-top: 1rem;
      margin-bottom: 2.4rem;
      width: 100%;
      align-self: flex-end;
    }
  }

  &.team-1 {
    background: var(--red-light);
    top: var(--sp-5);
  }

  &.team-2 {
    background: var(--red-mid);
    top: var(--sp-8);
  }

  &.team-3 {
    background: var(--red);
    top: var(--sp-13);

    h3 {
      color: var(--white);
    }

    .team {
      border-color: rgba(255, 255, 255, 0.2) !important;

      p {
        color: var(--white) !important;
      }
    }
  }

  @media ${media.M} {
    width: 70vw;

    h3 {
      font-size: 3.2rem;
    }

    .team-content {
      flex-direction: row;

      .text {
        order: 1;
        > div {
          max-width:50rem;
        }
      }

      figure {
        order: 2;
        margin-top: 0;
        width: 85%;
        align-self: flex-start;
      }
    }

    &.team-1 {
      transform: translateX(15vw);
    }

    &.team-2 {
      transform: translateX(10vw);
    }

    &.team-3 {
      transform: translateX(20vw);
    }
  }
`;

Team.propTypes = {
  members: PropTypes.array,
  style: PropTypes.object,
  className: PropTypes.string,
  headline: PropTypes.string,
  dark: PropTypes.bool,
};

export default Team;
